/**
 * Admin Portal → Partners: pieces every partner page shares — the program banner, the
 * admin's role (read from the server, never assumed), the page's notice line, the
 * typed-confirmation dialog and a few small layout helpers.
 *
 * Web-only (the admin SPA).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AdminButton, Badge } from '../../../components/admin/ui';
import { PartnersAdmin, PartnerApiError } from '../../../services/partnersAdminService';
import { canWrite, programBanner, typedConfirmationMatches, type ProgramStatus } from './partnersLogic';

// ── styles (the admin kit's tokens) ──────────────────────────────────────────

export const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border, #e5e7eb)',
  fontSize: 12, color: 'var(--text-secondary, #6b7280)', whiteSpace: 'nowrap',
};
export const td: React.CSSProperties = {
  padding: '8px 10px', borderBottom: '1px solid var(--border-subtle, #f3f4f6)', fontSize: 13, verticalAlign: 'top',
};
export const input: React.CSSProperties = {
  border: '1px solid var(--border-strong, #d1d5db)', borderRadius: 8, padding: '6px 10px', fontSize: 13,
  minWidth: 0, background: 'var(--surface, #fff)', color: 'var(--text-primary, #111827)',
};
export const label: React.CSSProperties = { display: 'block', fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 };
export const hint: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 4 };
export const row: React.CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' };

export function errText(err: unknown): string {
  if (err instanceof PartnerApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

// ── the admin's role ─────────────────────────────────────────────────────────

/**
 * The signed-in admin's role, from GET /api/admin/auth/me (the server's own answer).
 * Until it answers — or if it fails — `writable` is false: the write tools stay hidden
 * (fail closed; the server would refuse a support or viewer session with 403 anyway).
 */
export function useAdminRole(): { role: string | null; writable: boolean; loaded: boolean; email: string | null } {
  const [role, setRole] = useState<string | null>(null);
  // The signed-in admin's own address (a contacts test send goes to it; never a contact's).
  const [email, setEmail] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let live = true;
    PartnersAdmin.me()
      .then((r) => {
        if (!live) return;
        setRole(r?.admin?.role ?? null);
        setEmail(r?.admin?.email ?? null);
      })
      .catch(() => { if (live) { setRole(null); setEmail(null); } })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, []);
  return { role, writable: canWrite(role), loaded, email };
}

// ── the program's switches ───────────────────────────────────────────────────

export function useProgramStatus(): { status: ProgramStatus | null; error: string | null } {
  const [status, setStatus] = useState<ProgramStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    PartnersAdmin.programStatus()
      .then((s) => { if (live) setStatus(s); })
      .catch((e) => { if (live) setError(errText(e)); });
    return () => { live = false; };
  }, []);
  return { status, error };
}

export const ProgramBanner: React.FC<{ status: ProgramStatus | null; error: string | null }> = ({ status, error }) => {
  const lines = programBanner(status, error);
  if (!lines.length) return null;
  return (
    <div style={{ display: 'grid', gap: 8, marginBottom: 16 }} data-testid="partner-program-banner">
      {lines.map((l) => (
        <div
          key={l.text}
          role="status"
          style={{
            padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.45,
            background: l.tone === 'error' ? 'var(--status-error-bg, #fef2f2)' : 'var(--status-warning-bg, #fffbeb)',
            color: l.tone === 'error' ? 'var(--status-error-text, #991b1b)' : 'var(--status-warning-text, #92400e)',
          }}
        >
          {l.text}
        </div>
      ))}
    </div>
  );
};

// ── one notice line per page ─────────────────────────────────────────────────

export type Notice = { kind: 'ok' | 'error' | 'info'; text: string; extra?: string[] } | null;

export const NoticeLine: React.FC<{ notice: Notice; onClose?: () => void }> = ({ notice, onClose }) => {
  if (!notice) return null;
  const bg = notice.kind === 'error' ? 'var(--status-error-bg, #fef2f2)'
    : notice.kind === 'ok' ? 'var(--status-success-bg, #ecfdf5)' : 'var(--status-info-bg, #eff6ff)';
  const fg = notice.kind === 'error' ? 'var(--status-error-text, #991b1b)'
    : notice.kind === 'ok' ? 'var(--status-success-text, #065f46)' : 'var(--status-info-text, #1e40af)';
  return (
    <div role={notice.kind === 'error' ? 'alert' : 'status'} style={{ background: bg, color: fg, padding: '10px 14px', borderRadius: 10, fontSize: 13, margin: '8px 0 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ whiteSpace: 'pre-wrap' }}>{notice.text}</span>
        {onClose && (
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: fg, cursor: 'pointer' }} aria-label="Dismiss">
            ×
          </button>
        )}
      </div>
      {notice.extra && notice.extra.length > 0 && (
        <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
          {notice.extra.map((x) => <li key={x}>{x}</li>)}
        </ul>
      )}
    </div>
  );
};

/**
 * One in-flight guard per page: set SYNCHRONOUSLY on press, so a double click in the
 * same tick sends nothing twice (React state would still read stale).
 */
export function useBusy(): [boolean, <T>(fn: () => Promise<T>) => Promise<T | undefined>] {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setBusy(true);
    try {
      return await fn();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);
  return [busy, run];
}

// ── the typed confirmation (destructive actions, design §5.3) ────────────────

export const TypedConfirm: React.FC<{
  title: string;
  body: React.ReactNode;
  expected: string;
  confirmLabel: string;
  busy?: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ title, body, expected, confirmLabel, busy, danger = true, onConfirm, onCancel }) => {
  const [typed, setTyped] = useState('');
  const ok = typedConfirmationMatches(typed, expected);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
    >
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 12, padding: 20, width: 'min(520px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>{title}</h3>
        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>{body}</div>
        <label style={label} htmlFor="typed-confirm">
          Type <b>{expected}</b> to confirm
        </label>
        <input id="typed-confirm" style={{ ...input, width: '100%' }} value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <AdminButton variant="secondary" size="sm" onClick={onCancel} disabled={busy}>Cancel</AdminButton>
          <AdminButton variant={danger ? 'danger' : 'primary'} size="sm" onClick={onConfirm} disabled={!ok || busy}>
            {busy ? 'Working…' : confirmLabel}
          </AdminButton>
        </div>
      </div>
    </div>
  );
};

export const StatusBadge: React.FC<{ status: string; tone: 'success' | 'warning' | 'neutral' | 'error' | 'info' }> = ({ status, tone }) => (
  <Badge status={tone}>{status.replace(/_/g, ' ')}</Badge>
);

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The admin roles that read but never write (the server answers them 403 on every write). */
export const READ_ONLY_ROLES: readonly string[] = ['support', 'viewer'];

/**
 * Neutral while /auth/me is still answering (no "read-only" flash for an admin who can
 * write). Once it has answered: nothing for a writer; the read-only line for a support or
 * viewer session; and when the role could not be confirmed — /auth/me failed, or answered
 * no role or one this page does not know — it says exactly that (the tools stay hidden, fail
 * closed), never "your role is support or viewer" (Codes PR-1b, review N7).
 */
export const ReadOnlyNote: React.FC<{ writable: boolean; loaded: boolean; role?: string | null; subject?: string }> = ({
  writable,
  loaded,
  role,
  subject = 'partners, codes or statements',
}) => {
  if (!loaded) return <p style={{ ...hint, marginBottom: 12 }} aria-busy="true">Checking your admin role…</p>;
  if (writable) return null;
  if (!role || !READ_ONLY_ROLES.includes(role)) {
    return (
      <p style={{ ...hint, marginBottom: 12 }} role="status">
        {"Couldn't confirm your role — write tools are hidden. Reload the page, or sign in again."}
      </p>
    );
  }
  return (
    <p style={{ ...hint, marginBottom: 12 }}>
      {`Read-only: your admin role (${role}) can look but not change ${subject}.`}
    </p>
  );
};

/** What a page says when `saveBlob` could not hand the file to the browser (it answered false). */
export const SAVE_FAILED = "The file was ready but this browser couldn't save it. Nothing was downloaded — try again, or another browser.";

/**
 * Hand a downloaded file to the browser. The admin portal is web-only; the guard keeps
 * this inert anywhere there is no DOM (a test runner, a native bundle) — and then it answers
 * false, which the caller must surface (SAVE_FAILED), never treat as a download.
 */
export function saveBlob(blob: Blob, filename: string): boolean {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

/**
 * Open a tab SYNCHRONOUSLY, inside the click (a popup blocker allows that), before the
 * signing request; point it at the signed link once it arrives. Returns a function to call
 * with the URL, or with null to close the tab when the request failed.
 *
 * When the browser blocked the tab anyway, the link is handed to ``onBlocked`` so the page
 * can show it IN PLACE — never ``window.location`` (that would navigate the admin away from
 * the statement they are working on).
 */
export function openPendingTab(onBlocked?: (url: string) => void): (url: string | null) => void {
  if (typeof window === 'undefined') return () => undefined;
  const w = window.open('about:blank', '_blank');
  if (w) {
    try { w.opener = null; } catch { /* cross-origin guard: ignore */ }
  }
  return (url) => {
    if (!w) {
      if (url && onBlocked) onBlocked(url);
      return;
    }
    if (url) w.location.href = url;
    else w.close();
  };
}

export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return v.slice(0, 10);
}

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}
